import { useCallback, useMemo } from "react";
import { Popover } from "react-bootstrap";
import reactHtmlParser, { convertNodeToElement } from "react-html-parser";

import { DomElement } from "htmlparser2";
import { twMerge } from "tailwind-merge";

import EntityAnalyticsChart from "components/EntityAnalyticsChart";
import StickyOverlayTrigger from "components/StickyOverlayTrigger";
import useMediaQuery from "hooks/useMediaQuery";
import { ptSerif, roboto } from "utils/fonts";
import insightStyle from "styles/insightDetails.module.scss";

const RichContent = ({
  html,
  children,
  className,
  hidePreviewModal,
  excludeInsightStyle,
}: {
  html?: string;
  children?: React.ReactNode;
  className?: string;
  hidePreviewModal?: boolean;
  excludeInsightStyle?: boolean;
}) => {
  const { mobile } = useMediaQuery();

  const cleanedHtml = useMemo(() => {
    return html?.replace(/\xa0/g, " ") || ""; // remove &nbsp;
  }, [html]);

  const transform: any = useCallback(
    (node: DomElement, index: number) => {
      if (
        node.type === "tag" &&
        node.attribs?.style?.includes("px!important")
      ) {
        node.attribs.style = node.attribs.style.replace("px!important", "px");
        return convertNodeToElement(node, index, transform);
      }

      if (node.type === "tag" && node.name === "html") {
        console.log(node);
        node.name = "div";
        return convertNodeToElement(node, index, transform);
      }

      if (hidePreviewModal || mobile) return;

      if (node.type === "tag") {
        const citation = node.attribs?.["data-smartkarma-citation"];
        if (citation) {
          const [type, id] = citation.split(":");
          if (type === "entities") {
            return (
              <StickyOverlayTrigger
                placement="right"
                overlay={
                  <Popover
                    className={twMerge(
                      "left-[-9999px]", // add intitial postion outisde of viewport to fix flickering
                      "bg-white shadow-lg rounded",
                      "min-w-[300px]",
                      "border border-solid border-gray-200",
                      roboto.variable,
                      "font-roboto",
                      "z-[1999]"
                    )}
                  >
                    <Popover.Body>
                      <EntityAnalyticsChart
                        slug={id}
                        showTitle
                        titleClassName="font-bold"
                        priceClassName="leading-[1.4]"
                      />
                    </Popover.Body>
                  </Popover>
                }
              >
                <a
                  {...node.attribs}
                  className={twMerge(node.attribs.className, "inline relative")}
                >
                  {node.children?.map?.(convertNodeToElement)}
                </a>
              </StickyOverlayTrigger>
            );
          }
        }
      }
    },
    [hidePreviewModal, mobile]
  );

  const content = useMemo(() => {
    if (children) return;

    return reactHtmlParser(cleanedHtml, { transform });
  }, [children, cleanedHtml, transform]);

  if (children) {
    return (
      <div
        className={twMerge(
          !excludeInsightStyle && insightStyle.container,
          className
        )}
        suppressHydrationWarning
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        roboto.variable,
        ptSerif.variable,
        !excludeInsightStyle && insightStyle.container,
        className
      )}
    >
      <div suppressHydrationWarning>{content}</div>
    </div>
  );
};

export default RichContent;
